import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, FileText, Loader2, Upload } from 'lucide-react';
import {
  PRIVACY_NOTICE_MAX_PDF_BYTES,
  PRIVACY_NOTICE_MAX_PDF_MB,
  type PrivacyNoticeMeta,
  type PrivacyNoticeUploadErrorCode,
} from '@echo-work-platform/shared-types';
import { api, ApiError } from '@/lib/api-client';

const ENDPOINT = '/api/admin/privacy-notice';

const UPLOAD_ERROR_MESSAGES: Record<PrivacyNoticeUploadErrorCode, string> = {
  NO_FILE: 'Nem érkezett fájl.',
  EMPTY_FILE: 'Nem érkezett fájl.',
  NOT_A_PDF: 'A feltöltött fájl nem PDF.',
  FILE_TOO_LARGE: `A fájl nagyobb a megengedett ${PRIVACY_NOTICE_MAX_PDF_MB} MB-nál.`,
  NOTICE_UNCHANGED: 'Ez a dokumentum már az aktuális.',
  VERSION_CONFLICT: 'Közben másik admin tett közzé dokumentumot; töltsd újra az oldalt.',
};

function uploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413) return UPLOAD_ERROR_MESSAGES.FILE_TOO_LARGE;
    const known =
      err.code !== undefined ? UPLOAD_ERROR_MESSAGES[err.code as PrivacyNoticeUploadErrorCode] : undefined;
    return known ?? `A feltöltés nem sikerült (${err.status}).`;
  }
  return 'A feltöltés nem sikerült.';
}

async function sha256Hex(file: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function Compliance(): JSX.Element {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [digest, setDigest] = useState<{ file: File; same: boolean } | null>(null);
  const [loadedPdf, setLoadedPdf] = useState<{ noticeId: string; url: string | null } | null>(null);

  const meta = useQuery({
    queryKey: [ENDPOINT],
    queryFn: () => api.get<PrivacyNoticeMeta | null>(ENDPOINT),
  });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.upload<PrivacyNoticeMeta>(ENDPOINT, formData);
    },
    onSuccess: () => {
      setSelected(null);
      if (fileRef.current) fileRef.current.value = '';
    },
    onSettled: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: [ENDPOINT] });
    },
  });

  const noticeId = meta.data?.id ?? null;
  const loadedForCurrent = loadedPdf?.noticeId === noticeId ? loadedPdf : null;
  const pdfUrl = loadedForCurrent?.url ?? null;
  const pdfLoadFailed = loadedForCurrent !== null && loadedForCurrent.url === null;
  useEffect(() => {
    if (!noticeId) return;
    let cancelled = false;
    let url: string | null = null;
    api
      .blob(`${ENDPOINT}/${noticeId}/file`)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setLoadedPdf({ noticeId, url });
      })
      .catch(() => {
        if (!cancelled) setLoadedPdf({ noticeId, url: null });
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [noticeId]);

  function onFileChange(files: FileList | null): void {
    upload.reset();
    setClientError(null);
    setSelected(null);
    const file = files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setClientError('Csak PDF tölthető fel.');
      return;
    }
    if (file.size > PRIVACY_NOTICE_MAX_PDF_BYTES) {
      setClientError(UPLOAD_ERROR_MESSAGES.FILE_TOO_LARGE);
      return;
    }
    setSelected(file);
  }

  function openConfirm(): void {
    if (!selected) return;
    setConfirming(true);
    const file = selected;
    const currentSha = meta.data?.sha256;
    if (!currentSha) return;
    void sha256Hex(file).then((sha) => {
      if (sha !== null) setDigest({ file, same: sha === currentSha });
    });
  }

  const sameAsCurrent = digest !== null && digest.file === selected && digest.same;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-heading">Compliance</h2>

      <div className="card space-y-3 p-4">
        <h3 className="font-semibold text-heading">Adatkezelési tájékoztató közzététele</h3>
        <p className="text-sm text-muted-foreground">
          Feltölthető formátum: .pdf, legfeljebb {PRIVACY_NOTICE_MAX_PDF_MB} MB.
        </p>
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            aria-label="Tájékoztató PDF"
            className="text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-solid file:border-border-strong file:bg-card file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground file:transition-colors hover:file:bg-muted"
            onChange={(e) => onFileChange(e.target.files)}
            disabled={upload.isPending}
          />
          <button className="btn-primary" disabled={!selected || upload.isPending} onClick={openConfirm}>
            {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Mentés
          </button>
        </div>
        {clientError && <p className="text-sm text-rose-600">{clientError}</p>}
        {upload.isError && <p className="text-sm text-rose-600">{uploadErrorMessage(upload.error)}</p>}
      </div>

      {/* isSuccess, not !isLoading: a failed fetch must not claim that users
          are running without acceptance — that is a compliance statement. */}
      {meta.isSuccess && !meta.data && (
        <div className="card flex items-start gap-3 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Nincs közzétett adatkezelési tájékoztató — a felhasználók jelenleg elfogadás nélkül használják a
            rendszert.
          </div>
        </div>
      )}

      <div className="card space-y-3 p-4">
        <h3 className="font-semibold text-heading">Aktuális dokumentum</h3>
        {meta.isLoading ? (
          <p className="text-sm text-muted-foreground">Betöltés…</p>
        ) : meta.isError ? (
          <p className="text-sm text-rose-600">A tájékoztató állapotát nem sikerült betölteni.</p>
        ) : !meta.data ? (
          <p className="text-sm text-muted-foreground">Még nincs közzétett adatkezelési tájékoztató.</p>
        ) : (
          <div className="flex flex-wrap gap-6">
            <div
              className="overflow-hidden rounded border border-border"
              style={{ width: 'min(100%, 794px)', aspectRatio: '210 / 297' }}
            >
              {pdfUrl ? (
                // #toolbar=0 hides the viewer toolbar (annotation/ink, print, save);
                // there is no parameter to hide individual tools only.
                <iframe
                  src={`${pdfUrl}#toolbar=0`}
                  title="Adatkezelési tájékoztató"
                  className="h-full w-full"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  <FileText className="mr-2 h-4 w-4" />
                  {pdfLoadFailed ? 'A dokumentum betöltése nem sikerült.' : 'A dokumentum betöltése…'}
                </div>
              )}
            </div>
            <dl className="space-y-1.5 text-sm">
              <div>
                <dt className="font-medium text-heading">Verzió</dt>
                <dd>{meta.data.version}</dd>
              </div>
              <div>
                <dt className="font-medium text-heading">Fájlnév</dt>
                <dd>{meta.data.filename}</dd>
              </div>
              <div>
                <dt className="font-medium text-heading">Méret</dt>
                <dd>{formatMb(meta.data.sizeBytes)}</dd>
              </div>
              <div>
                <dt className="font-medium text-heading">SHA-256</dt>
                <dd className="max-w-64 break-all font-mono text-xs">{meta.data.sha256}</dd>
              </div>
              <div>
                <dt className="font-medium text-heading">Közzététel ideje</dt>
                <dd>{new Date(meta.data.publishedAt).toLocaleString('hu-HU')}</dd>
              </div>
              {pdfUrl && (
                <div className="pt-2">
                  <a href={pdfUrl} download={meta.data.filename} className="btn-primary inline-flex">
                    <Download className="h-4 w-4" /> Letöltés
                  </a>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>

      {confirming && selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay)' }}
        >
          <div role="dialog" aria-modal="true" className="card w-full max-w-lg p-6">
            <h3 className="mb-3 text-lg font-semibold text-heading">Tájékoztató közzététele</h3>
            <p className="text-sm">
              Közzéteszed a(z) <span className="font-medium">{selected.name}</span> fájlt (
              {formatMb(selected.size)})? A közzététel után minden felhasználónak újra el kell fogadnia a
              tájékoztatót.
            </p>
            {sameAsCurrent && (
              <p className="mt-3 flex items-start gap-2 text-sm text-amber-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Ez a fájl bájtra azonos az aktuális dokumentummal — a szerver el fogja utasítani.
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="btn-secondary"
                onClick={() => setConfirming(false)}
                disabled={upload.isPending}
              >
                Mégse
              </button>
              <button
                className="btn-primary"
                onClick={() => upload.mutate(selected)}
                disabled={upload.isPending}
              >
                {upload.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Közzététel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
